if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        const nativeArtwork = window.NKSNativeArtwork;
        const nativeFile = this.form.querySelector('[data-nks-native-artwork]');
        if (nativeArtwork?.hasSelectedFile(this.form) || nativeFile?.files?.[0]) {
          void this.addNativeArtwork(evt, nativeArtwork);
          return;
        }
        const fastCart = window.nksFastCart;
        if (fastCart?.add) {
          const formData = this.productFormData();
          if (!formData.get('id')) return;
          this.cart?.setActiveElement(document.activeElement);
          void fastCart.add(formData, {
            button: this.submitButton,
            source: 'product-form',
            cartUI: this.cart,
            onSuccess: () => {
              this.error = false;
              this.closest('quick-add-modal')?.hide(true);
            },
            onError: (error, response) => {
              this.handleErrorMessage(error.message);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response?.errors || error.message,
                message: response?.message,
              });
            },
          });
          return;
        }
        const finishCartOperation = fastCart?.begin();
        const feedback = document.querySelector('.nks-commerce-feedback');
        fastCart?.showFeedback(feedback?.dataset.loadingMessage, 'loading');

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = this.productFormData();
        if (fastCart) {
          formData.delete('return_to');
          formData.delete('sections');
          formData.delete('sections_url');
          this.cart?.setActiveElement(document.activeElement);
        } else if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              fastCart?.showFeedback(response.description || feedback?.dataset.errorMessage, 'error');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              finishCartOperation?.();
              window.location = window.routes.cart_url;
              return;
            }

            if (fastCart) {
              this.error = false;
              fastCart.showFeedback(feedback?.dataset.successMessage, 'success');
              const quickAddModal = this.closest('quick-add-modal');
              const finish = () => finishCartOperation({ source: 'product-form', productVariantId: formData.get('id'), cartData: response, cartUI: this.cart });
              if (quickAddModal) {
                quickAddModal.hide(true);
              }
              finish();
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            fastCart?.showFeedback(feedback?.dataset.errorMessage, 'error');
            console.error(e);
          })
          .finally(() => {
            finishCartOperation?.();
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      productFormData() {
        const data = new FormData(this.form);
        const nativeFile = this.form.querySelector('[data-nks-native-artwork]');
        if (nativeFile && !nativeFile.files?.[0]) data.delete(nativeFile.name);
        return data;
      }

      nativeArtworkMessage(kind, error) {
        const spanish = document.documentElement.lang.toLowerCase().startsWith('es');
        if (kind === 'unavailable') return spanish
          ? 'No se puede adjuntar el archivo. Conserva el original y recarga esta página.'
          : 'Artwork attachment is unavailable. Keep your original file and reload this page.';
        if (kind === 'refresh') return spanish
          ? 'El archivo se verificó en el carrito. Abre el carrito para revisarlo antes de continuar.'
          : 'The artwork was verified in your cart. Open the cart to review it before continuing.';
        if (error?.code === 'cart_recovery_required') return spanish
          ? 'No pudimos verificar o retirar el archivo del carrito. No vuelvas a intentar. Conserva el original y contacta a NKS para revisar el carrito.'
          : error.message || 'The artwork attachment needs review. Do not retry. Keep your original file and contact NKS.';
        if (error?.cleanupConfirmed === true) return spanish
          ? 'No pudimos verificar el archivo. Se retiró su nuevo artículo del carrito. No se envió ningún pedido. Conserva el original.'
          : error.message || 'The artwork could not be verified. Its new cart item was removed. No order was submitted.';
        return spanish
          ? 'No pudimos adjuntar el archivo. Conserva el original y revisa el carrito antes de volver a intentar.'
          : error?.message || 'The artwork could not be attached. Keep your original file and review the cart before retrying.';
      }

      async addNativeArtwork(evt, nativeArtwork) {
        if (this.form.dataset.nksNativeArtworkPending === 'true') return;
        const fileInput = this.form.querySelector('[data-nks-native-artwork]');
        const selectedFile = fileInput?.files?.[0];
        const removeButton = this.form.querySelector('.fs-upload__remove');
        const spinner = this.querySelector('.loading__spinner');
        const variantId = this.variantIdInput.value;
        const inputDisabled = fileInput?.disabled;
        const removeDisabled = removeButton?.disabled;
        this.form.dataset.nksNativeArtworkPending = 'true';
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.form.setAttribute('aria-busy', 'true');
        spinner?.classList.remove('hidden');
        if (fileInput) fileInput.disabled = true;
        if (removeButton) removeButton.disabled = true;
        this.cart?.setActiveElement(document.activeElement);
        let verified = false;
        try {
          if (!nativeArtwork?.fromDocument) throw new Error(this.nativeArtworkMessage('unavailable'));
          const service = nativeArtwork.fromDocument();
          const sections = this.cart?.getSectionsToRender().map((section) => section.id) || [];
          const result = await service.addProduct(this.form, { sections, sectionsUrl: window.location.pathname });
          if (result?.status !== 'cart_item_verified' || !Array.isArray(result.cart?.items)) {
            throw new Error(this.nativeArtworkMessage('unavailable'));
          }
          verified = true;
          fileInput?.dispatchEvent(new CustomEvent('nks:artwork-cart-verified', {
            bubbles: true,
            detail: { file: selectedFile },
          }));
          this.error = false;
          this.closest('quick-add-modal')?.hide(true);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'nks-native-artwork',
              productVariantId: variantId,
              cartData: result.cart,
            });
          }
          if (this.cart) {
            let response = result.response;
            if (!response?.sections || !sections.every((id) => typeof response.sections[id] === 'string')) {
              const address = new URL(window.location.pathname, window.location.origin);
              address.searchParams.set('sections', sections.join(','));
              const rendered = await fetch(address.pathname + address.search, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                cache: 'no-store',
              });
              if (!rendered.ok) throw new Error('Cart sections unavailable');
              response = { ...result.line, sections: await rendered.json() };
            }
            if (!sections.every((id) => typeof response.sections?.[id] === 'string')) {
              throw new Error('Cart sections incomplete');
            }
            this.cart.renderContents(response);
            this.cart.classList.remove('is-empty');
          }
        } catch (error) {
          const message = verified ? this.nativeArtworkMessage('refresh') : this.nativeArtworkMessage('error', error);
          this.handleErrorMessage(message);
          if (!verified && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'nks-native-artwork',
              productVariantId: variantId,
              errors: message,
              message,
            });
          }
        } finally {
          delete this.form.dataset.nksNativeArtworkPending;
          this.form.removeAttribute('aria-busy');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
          if (fileInput) fileInput.disabled = inputDisabled;
          if (removeButton) removeButton.disabled = removeDisabled;
          if (typeof CartPerformance !== 'undefined') CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
